/**
 * Manufacturer Anomaly Flags Page
 * Lets manufacturers view and manage flags on their own products/batches.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { anomalyFlagService, AnomalyFlag, AnomalyFlagFilters } from '../services/anomaly-flag.service';

const ManufacturerAnomalyFlagsPage: React.FC = () => {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<AnomalyFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters] = useState<AnomalyFlagFilters>({ limit: 20, offset: 0 });
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadFlags();
  }, [filters]);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await anomalyFlagService.getFlags(filters);
      setFlags(result.flags);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load anomaly flags');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (flagId: string, action: 'acknowledged' | 'dismissed' | 'reopen') => {
    try {
      setError(null);
      setSuccess(null);
      if (action === 'reopen') {
        await anomalyFlagService.reopenFlag(flagId);
        setSuccess('Flag reopened');
      } else {
        await anomalyFlagService.updateFlagStatus(flagId, action);
        setSuccess(`Flag ${action}`);
      }
      loadFlags();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update flag');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading anomaly flags...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Anomaly Flags</h1>
            <p className="text-sm text-gray-600">Flags for products under your manufacturer</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/manufacturer')}>Back to Dashboard</Button>
            <Button variant="outline" onClick={loadFlags}>Refresh</Button>
          </div>
        </div>

        {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}
        {success && <div className="bg-green-50 text-green-700 p-3 rounded">{success}</div>}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">
                Showing {flags.length} flag{flags.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {flags.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No anomaly flags found for your products</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" aria-label="Manufacturer anomaly flags">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product / Risk</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reasons</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {flags.map(flag => {
                    const productName = flag.product?.batch?.productName || flag.product?.name || 'Product';
                    const serialLabel = flag.product?.serialNumber ? `Serial #${flag.product.serialNumber}` : null;
                    const batchLabel = flag.product?.batch?.id || flag.product?.batchId;
                    const packageLabel = flag.product?.packages && flag.product.packages.length > 0
                      ? flag.product.packages[0]
                      : null;

                    return (
                      <tr key={flag.id}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-gray-900">
                            {productName}
                          </div>
                          <div className="text-xs text-gray-500">
                            Risk: {flag.riskLevel} | Score {flag.riskScore}
                          </div>
                          <div className="text-xs text-gray-500">
                            {serialLabel || 'Serial unknown'}
                          </div>
                          {batchLabel && (
                            <div className="text-xs text-gray-500">
                              Batch: {batchLabel}
                            </div>
                          )}
                          {flag.product?.outerQrToken && (
                            <div className="text-xs text-gray-500">
                              QR: {flag.product.outerQrToken}
                            </div>
                          )}
                          {packageLabel && (
                            <div className="text-xs text-gray-500">
                              Package: {packageLabel.packageType} ({packageLabel.qrToken})
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                          {flag.status}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {flag.reasons?.slice(0, 2).join('; ') || 'No reasons provided'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(flag.firstSeen || flag.lastSeen).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-2">
                            {flag.status !== 'acknowledged' && (
                              <Button size="sm" variant="outline" onClick={() => handleAction(flag.id, 'acknowledged')}>
                                Acknowledge
                              </Button>
                            )}
                            {flag.status !== 'dismissed' && (
                              <Button size="sm" variant="outline" onClick={() => handleAction(flag.id, 'dismissed')}>
                                Dismiss
                              </Button>
                            )}
                            {flag.status !== 'open' && (
                              <Button size="sm" variant="outline" onClick={() => handleAction(flag.id, 'reopen')}>
                                Reopen
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </div>
  );
};

export default ManufacturerAnomalyFlagsPage;

